import React, { useContext, useEffect, useState, useRef } from 'react';

/* eslint-disable max-len,object-curly-newline */
// THD NUCLEUS
import {
  extend, params, string, useDataModel, customType,
  shape, bool, arrayOf, QueryContext, QueryProvider, alias
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { PIPRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { useTheme } from '@thd-olt-component-react/theme-provider';

// THD Components (alphabetical by package name)
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { Avataar, RoomVoInspire, RoomVo } from '@thd-olt-component-react/augmented-reality';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { MajorApplianceBuyBox } from '@thd-olt-component-react/buybox';
import { CompatibleAccessories } from '@thd-olt-component-react/compatible-accessories';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Col, Row, Placeholder } from '@thd-olt-component-react/core-ui';
import { DeliveryAndInstallation } from '@thd-olt-component-react/delivery-and-installation';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryWithHotspotsDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Link } from '@one-thd/sui-atomic-components';
import { ProductConfigurator } from '@thd-olt-component-react/product-configurator';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductOverviewAccordion } from '@thd-olt-component-react/product-overview';
import { Promotion } from '@thd-olt-component-react/promotion';
import { PromotionProductsAnchor } from '@thd-olt-component-react/promotion-products';
import { QuestionsAndAnswersAccordion } from '@thd-olt-component-react/questions-and-answers';
import { RatingsAndReviewsAccordion } from '@thd-olt-component-react/ratings-and-reviews';
import { RelatedSearchImporter, PopularVariationsImporter } from '@thd-olt-component-react/related-search';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { BazaarVoiceCarousel } from '@thd-olt-component-react/social-media-container';
import { SpecificationsAccordion } from '@thd-olt-component-react/specifications'
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import {
  DynamicRecsWrapper,
  PipSemWrapper,
  RecentlyViewedWrapper
} from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { WhatWeOffer } from '@thd-olt-component-react/what-we-offer';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { ProductPageEventCountdownTimer } from '@thd-olt-component-react/event-countdown-timer';
import { BundlePmvFbtLayoutMa } from '@thd-olt-component-react/bundle-mini';

// Project Files
import { PackagesMiniABTest } from '../../components/PackagesMiniABTest';
import { MagicApronBotPrompt, MagicApronBotPromptLoad } from './dynamic-imports';
import { EmtLinksLoad, EmtLinks, EmtLinksModel } from '../shared-dynamic-imports';
import {
  shouldDisplayVisuallySimilar,
  shouldDisplayIrgaccessories,
  shouldDisplayIrgcollections,
  getProductTaxonomy,
  shouldDisplayBBTBelow,
  shouldDisplayBBTAbove
} from './product-utils';
import {
  updateStickyHeader,
  updateHeaderConfiguration,
} from '../utils';
import {
  HYDRATION_STANDARD_SCROLL_BUFFER,
  HYDRATION_STANDARD_DELAY_TIMER,
  RECS_REQUEST_KEY,
  STICKY_HEADER_HEIGHT,
  ONLINE_STORE_ID
} from '../../constants';
import ZoneCard from '../../components/zone-card';
import { useAutoScrollToId } from '../../hooks/useAutoScrollToId';
import { useShouldDisplayReferrerRecommendations } from '../../hooks/useShouldDisplayReferrerRecommendations';
import '../../styles/product-page.scss';

/* eslint-enable max-len,object-curly-newline */

export const Product = () => {
  declareContexts([
    QueryContext,
    ExperienceContext,
    CustomerContext,
  ]);
  const thdCustomer = useThdCustomer();
  const ctx = useContext(ExperienceContext);
  const { deliveryZip } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const qctx = useContext(QueryContext);
  const isB2BCustomer = ctx?.customer?.type === 'b2b';
  const path = ctx?.path || '';
  const { itemId } = useParams();
  const storeId = useStoreId();
  let navigate = useNavigate();
  const { pathname } = useLocation();
  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);
  const { isExchangeCustomer } = useThdCustomer() || {};

  const theme = useTheme(Product);

  const {
    hideConsumerCardMessaging,
    hideSponsoredCarousel,
    hideSponsoredPipBanner,
    hideShareBeltShareButton,
    hideAddToList,
    hideAisleBay,
    hideYouMightAlsoNeed,
    hideRelatedProducts
  } = theme.props;

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || storeId === ONLINE_STORE_ID || !storeId,
    ssr: false
  });

  // Call the checkAvailability API for exchange customers only for major appliances
  const { data: availabilityData } = useDataModel('checkAvailability', {
    variables: {
      itemIds: [itemId],
      zipCode: deliveryZip,
      localStoreId: storeId
    },
    ssr: false,
    skip: !isExchangeCustomer || !itemId || !deliveryZip || !storeId
  });

  useEffect(() => {
    updateStickyHeader('Header3-sticky');
    updateHeaderConfiguration('default');
  }, []);

  useAutoScrollToId(STICKY_HEADER_HEIGHT.DESKTOP);

  const magicApronQABotEnabled = useConfigService('fs-prop:magic-apron-qa-bot-enabled');
  const pipRecsPositionAbove = useConfigService('fs:pipRecsPositionAbove');
  const pipRecsPositionBelow = useConfigService('fs:pipRecsPositionBelow');
  const pipRecsPositionDefault = useConfigService('fs:pipRecsPositionDefault');
  const hideFBT = useConfigService('fs:hideFBT');
  const hideYMAN = useConfigService('fs:hideYMAN');
  const hideBBT = useConfigService('fs:hideBBT');
  const isPipRecsAbTest = pipRecsPositionAbove || pipRecsPositionBelow || pipRecsPositionDefault;
  const referer = useContext(ExperienceContext)?.referer;
  const shouldDisplayReferrerRecommendations = useShouldDisplayReferrerRecommendations(referer, path);
  const shouldDisplayReferrerRecsAbove = (shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionAbove;
  const shouldDisplayReferrerRecsBelow = pipRecsPositionBelow;
  const shouldDisplayReferrerRecsDefault = (!shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionDefault;

  const onSuperSkuChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { state: { disableScroll: true } });
    }
  };

  const openAccordion = (accordion) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger(`${accordion}.click`, false);
  };

  const handleViewMoreDetails = (event) => {
    event.preventDefault();
    openAccordion('product-section-overview');
    window.scrollTo({
      top: productOverviewRef.current.offsetTop - STICKY_HEADER_HEIGHT.DESKTOP,
      behavior: 'smooth'
    });
  };

  const productTaxonomy = getProductTaxonomy(productData);

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound">
          <Row>
            <ErrorBoundary name="sticky-nav">
              <StickyNav
                itemId={itemId}
                ref={stickyNavRef}
                ratingsRef={ratingsRef}
                onRatingsClick={() => openAccordion('product-section-rr')}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => openAccordion('product-section-overview')}
                      role="button"
                      tabIndex={0}
                    >Product Details
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => openAccordion('product-section-key-feat')}
                      role="button"
                      tabIndex={0}
                    >Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => openAccordion('product-section-qa')}
                      role="button"
                      tabIndex={0}
                    >Questions & Answers
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={ratingsRef}>
                    <div
                      className="navlink-rr"
                      onClick={() => openAccordion('product-section-rr')}
                      role="button"
                      tabIndex={0}
                    >Customer Reviews
                    </div>
                  </StickyNav.Link>
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>
          <Row>
            <ErrorBoundary name="breadcrumbs-product-info-bar">
              <Col sm="7">
                <Breadcrumbs itemId={itemId} hideOnNoCategory={false} />
              </Col>
              <Col sm="5" className="u__flex-end u__paddingRightNone">
                <ProductInfoBar itemId={itemId} />
              </Col>
            </ErrorBoundary>
          </Row>
          {shouldDisplayReferrerRecsAbove && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                withReferrer={shouldDisplayReferrerRecommendations}
              />
            </ZoneCard>
          )}
          <Row id="zone-a">
            <ErrorBoundary name="zone-a">
              <Col nopadding>
                <Row>
                  <Col>
                    <ErrorBoundary name="discontinued">
                      <Discontinued itemId={itemId} />
                    </ErrorBoundary>
                  </Col>
                  <Col sm="7" flatten className="product--sticky">
                    <ZoneCard className="u--paddingBottom overflow u__clearfix ">
                      <Col sm="12">
                        <ErrorBoundary name="product-details">
                          <ProductDetails itemId={itemId}>
                            <ProductDetails.Badge itemId={itemId} />
                            <ProductDetails.BrandCollection showCollection showBrandHyperLink />
                            <div className="product-details__badge-title--wrapper">
                              <ProductDetails.Title />
                            </div>
                            {!hideAddToList && (
                              <div className="product-details__favorites sui-my-2">
                                <Sharebelt itemId={itemId}>
                                  <ShareFavorite showCircle showFavoritesCount />
                                </Sharebelt>
                              </div>
                            )}
                            <ProductDetails.Ratings
                              showBadge
                              target="#product-section-rr"
                              offsetselector="#sticky-nav"
                              qaselector="#product-section-qa"
                              onClick={() => openAccordion('product-section-rr')}
                            />
                          </ProductDetails>
                        </ErrorBoundary>
                      </Col>
                      <ErrorBoundary name="media-gallery">
                        <MediaGalleryWithHotspotsDesktop
                          itemId={itemId}
                          mainImageFetchPriority="high"
                          showLoader
                        />
                      </ErrorBoundary>
                      <ErrorBoundary name="sharebelt">
                        <Sharebelt horizontal itemId={itemId}>
                          {!hideShareBeltShareButton && <ShareBeltShareButton email />}
                          <ShareBeltPrint />
                        </Sharebelt>
                      </ErrorBoundary>
                    </ZoneCard>

                    <ErrorBoundary name="AvataarAR">
                      <ZoneCard className="overflow ar-container col__12-12">
                        <Avataar itemId={itemId} />
                        <RoomVo itemId={itemId} />
                      </ZoneCard>
                    </ErrorBoundary>
                  </Col>
                  <Col sm="5" flatten>
                    <Row className="overflow zone-a">
                      <ZoneCard nopadding tag={Col}>
                        <ErrorBoundary name="event-countdown-timer">
                          <ProductPageEventCountdownTimer itemId={itemId} />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="price">
                          <Price
                            channel="desktop"
                            displayEachUom={false}
                            itemId={itemId}
                            large
                            disableRangePricing
                            clsRemediation={{
                              placeholders: true,
                              preservePlaceholders: true
                            }}
                            showPreferredPricingBadge
                            showPreferredPricingBadgeToolTip
                            availabilityData={availabilityData}
                          />
                        </ErrorBoundary>
                        <ErrorBoundary name="promotion">
                          <Col className="overflow promotion__card">
                            <Promotion
                              hideConditionalPromoDescription
                              itemId={itemId}
                            />
                          </Col>
                        </ErrorBoundary>
                        {!hideConsumerCardMessaging && (
                          <div className="consumer-credit-messaging__card">
                            <ConsumerCardMessaging
                              itemId={itemId}
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                              isB2B={isB2BCustomer}
                            />
                          </div>
                        )}
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="promotion-products">
                          <PromotionProductsAnchor
                            itemId={itemId}
                            isMultiSelect={(product) => !!product?.pricing?.conditionalPromotions?.[0]?.nvalues?.length}
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="salient-points">
                          <SalientPoints itemId={itemId} max={3}>
                            <SalientListItem>
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <Link
                                underline="always"
                                onClick={handleViewMoreDetails}
                              >
                                View More Details
                              </Link>
                            </SalientListItem>
                          </SalientPoints>
                        </ErrorBoundary>
                      </ZoneCard>
                      {!hideAisleBay && (
                        <ZoneCard tag={Col}>
                          <ErrorBoundary name="aisle-bay">
                            <AisleBay itemId={itemId} />
                          </ErrorBoundary>
                        </ZoneCard>
                      )}
                      <ZoneCard tag={Col} className="overflow">
                        <ErrorBoundary name="product-configurator">
                          {productData?.product?.identifiers?.productType === 'CONFIGURABLE_BLINDS' && (
                            <ProductConfigurator itemId={itemId} />
                          )}
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} className="overflow">
                        <ErrorBoundary name="super-sku">
                          <SuperSku
                            itemId={itemId}
                            onChange={onSuperSkuChange}
                            disableMediaAndAvailability
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} nopadding>
                        <ErrorBoundary name="fit-compatibility">
                          <FitCompatibility
                            type="PIP"
                            itemId={itemId}
                            imageCacheEnabled
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} nopadding>
                        <MajorApplianceBuyBox
                          itemId={itemId}
                          sticky
                          showCards
                          showFavorites={isB2BCustomer}
                          showAddToProject={isB2BCustomer}
                        />
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="compatible-accessories">
                          <CompatibleAccessories
                            itemId={itemId}
                            parent="PIP"
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <Col>
                        <ErrorBoundary name="global-custom-configurator">
                          <GlobalCustomConfigurator itemId={itemId} />
                        </ErrorBoundary>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </ErrorBoundary>
          </Row>
          {shouldDisplayReferrerRecsBelow && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                withReferrer={shouldDisplayReferrerRecommendations}
              />
            </ZoneCard>
          )}
          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    {[
                      { id: 'product-section-overview', ref: productOverviewRef },
                      { id: 'product-section-key-feat', ref: specificationsRef },
                      { id: 'product-section-qa', ref: questionsRef },
                      { id: 'product-section-rr', ref: ratingsRef },
                    ].map(({ id, ref }) => (
                      <div id={id} ref={ref} className="accordion-placeholder" key={id}>
                        <Placeholder width="100%" height="82px" color="#F5F5F5" />
                      </div>
                    ))}
                  </>
                )}
              >
                <ErrorBoundary name="packages-mini-in-collapsible">
                  <BundlePmvFbtLayoutMa itemId={itemId} showPackagesMini />
                </ErrorBoundary>
                {shouldDisplayBBTAbove(hideFBT, hideBBT, productData) && (
                  <Row>
                    <DynamicRecsWrapper
                      schemaName="beyond_bought_together"
                      anchorId={itemId}
                      requestKey={RECS_REQUEST_KEY}
                      isBBT
                      errorBoundary
                      hideFavorites
                      hideSwatches
                      hydrator={{
                        className: 'grid',
                        tag: ZoneCard,
                        id: 'beyond_bought_together',
                        delay: 1500,
                        scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                      }}
                    />
                  </Row>
                )}
                <div className="sui-flex sui-flex-col sui-w-full">
                  <div
                    id="product-section-overview"
                    className="product-section-overview"
                    ref={productOverviewRef}
                  >
                    <ProductOverviewAccordion itemId={itemId} />
                  </div>
                  <div
                    id="product-section-key-feat"
                    className="product-section-key-feat"
                    ref={specificationsRef}
                  >
                    <SpecificationsAccordion
                      displaySimilarLinks={false}
                      isWhiteSpecTitle
                      itemId={itemId}
                      oneColumn
                      withKeyProductFeatures
                    />
                  </div>
                  <div
                    id="product-section-qa"
                    className="product-section-qa"
                    ref={questionsRef}
                  >
                    <QuestionsAndAnswersAccordion itemId={itemId} noSpacing />
                  </div>
                  <div
                    id="product-section-rr"
                    className="product-section-rr"
                    ref={ratingsRef}
                  >
                    <RatingsAndReviewsAccordion
                      itemId={itemId}
                      noSpacing
                      prosAndCons
                      disableOverlayOnClick={isB2BCustomer}
                    />
                  </div>
                </div>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>
          {magicApronQABotEnabled && !isB2BCustomer && (
            <ErrorBoundary name="magic-apron-qa-bot">
              <DynamicComponent pageType="pip">
                <Hydrator
                  id="product-section-magic-apron-qa-bot"
                  className="grid"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  waitFor={MagicApronBotPromptLoad}
                  placeholder={<Placeholder width="100%" height="276px" color="#F5F5F5" />}
                >
                  <MagicApronBotPrompt qctx={qctx} itemId={itemId} />
                </Hydrator>
              </DynamicComponent>
            </ErrorBoundary>
          )}

          <ErrorBoundary name="bv-social-media-container">
            <DynamicComponent pageType="pip">
              <div className="sui-w-full sui-my-2 empty:sui-my-0">
                <BazaarVoiceCarousel
                  itemId={itemId}
                  siteName="homedepot-gqfoln"
                  lazyLoadGallery
                />
              </div>
            </DynamicComponent>
          </ErrorBoundary>

          <ErrorBoundary name="roomvo-inspire-banner">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="product-section-roomvo-inspire-banner"
                className="grid"
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                placeholder={<Placeholder width="100%" height="150px" color="#F5F5F5" />}
              >
                <Row className="sui-pt-2 sui-pb-6">
                  <RoomVoInspire />
                </Row>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          {!hideSponsoredPipBanner && (
            <SponsoredPipBanner
              browserId={thdCustomer?.mcvisID}
              pageContext={{
                label: 'pip',
                data: {
                  itemId,
                  treatment: 'A'
                },
              }}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                id: 'hydrated-sponsored-pip-banner-A',
                placeholder: (
                  <Placeholder
                    width="100%"
                    height="144px"
                    color="#f5f5f5"
                  >
                    <div />
                  </Placeholder>
                )
              }}
            />
          )}
          {!isB2BCustomer && (
            <>
              <ZoneCard>
                <ErrorBoundary name="what-we-offer">
                  <WhatWeOffer itemId={itemId} />
                </ErrorBoundary>
              </ZoneCard>
              <ZoneCard>
                <ErrorBoundary name="delivery-and-installation">
                  <DeliveryAndInstallation itemId={itemId} />
                </ErrorBoundary>
              </ZoneCard>
            </>
          )}
          {shouldDisplayBBTBelow(hideFBT, hideYMAN, hideBBT, productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="beyond_bought_together"
                anchorId={itemId}
                requestKey={RECS_REQUEST_KEY}
                isBBT
                errorBoundary
                hideFavorites
                hideSwatches
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'beyond_bought_together',
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </Row>
          )}

          {shouldDisplayReferrerRecsDefault && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pip_alternatives"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                recsContext={productTaxonomy}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: pipRecsPositionDefault ? 'RecsDefault' : 'pip_alternatives'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          {!hideSponsoredCarousel && (
            <RetailMediaCarousel
              breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
              pageContext={{
                schema: 'pip_sponsored',
                data: { itemId },
                isPip: true
              }}
              showSponsoredCarousel
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}

          <ErrorBoundary name="feature-based-recommendations">
            <FeatureBasedRecommendationsImporter
              hideOnMobile
              itemId={itemId}
              qctx={qctx}
              errorBoundary
              hydrator={{
                tag: ZoneCard,
                className: 'grid'
              }}
              dynamic={{
                pageType: 'pip'
              }}
            />
          </ErrorBoundary>

          {shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="visuallysimilar_1_0"
                anchorImage={productData?.product?.media?.image?.url}
                anchorId={itemId}
                errorBoundary
                checkMinimumProducts
                minimumProductCount={4}
                requestKey={RECS_REQUEST_KEY}
                recsContext={productTaxonomy}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'visuallysimilar_1_0'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          {shouldDisplayIrgcollections(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="brand_based_collection"
                anchorId={itemId}
                isIRG
                isCategoryTab
                maxResults="18"
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideFavorites={false}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'recs_brand_based_collection'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          {shouldDisplayIrgaccessories(hideYMAN, productData) && !hideYouMightAlsoNeed && (
            <Row>
              <DynamicRecsWrapper
                schemaName="irgaccessories"
                anchorId={itemId}
                isIRG
                maxResults="18"
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                recsContext={productTaxonomy}
                hideFavorites={false}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'recs_irgaccessories'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          <QueryProvider
            defaultVariables={defaultVariables?.current}
            persist
            cacheKey="rv-pip-recs"
          >
            <RecentlyViewedWrapper
              schemaName="rv_gm_pip_rr"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 2000,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                id: 'rv_gm_pip_rr'
              }}
            />
          </QueryProvider>
          {!hideRelatedProducts && (
            <RelatedSearchImporter
              itemId={itemId}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'col__12-12 sui-mb-4'
              }}
            />
          )}
          <PopularVariationsImporter
            productId={itemId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12 sui-mb-4'
            }}
          />

          <DynamicComponent pageType="pip">
            <Hydrator
              id="product-section-seo"
              className="grid u__flex-center"
              delay={2000}
              placeholder={(
                <Placeholder
                  width="100%"
                  height="689px"
                  color="#f5f5f5"
                >
                  <div />
                </Placeholder>
              )}
              preserveCtxVal="clientStore"
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              waitFor={[EmtLinksLoad]}
            >
              <EmtLinks path={pathname} qctx={qctx} />
            </Hydrator>
          </DynamicComponent>
        </Row>
      </ErrorBoundary>
    </>
  );
};

Product.displayName = 'ProductPage';

Product.themeProps = {
  hideConsumerCardMessaging: bool,
  hideSponsoredCarousel: bool,
  hideSponsoredPipBanner: bool,
  hideShareBeltShareButton: bool,
  hideAddToList: bool,
  hideAisleBay: bool,
  hideYouMightAlsoNeed: bool,
  hideRelatedProducts: bool
};

Product.defaultThemeProps = {
  hideConsumerCardMessaging: false,
  hideSponsoredCarousel: false,
  hideSponsoredPipBanner: false,
  hideShareBeltShareButton: false,
  hideAddToList: false,
  hideAisleBay: false,
  hideYouMightAlsoNeed: false,
  hideRelatedProducts: false
};

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      })
    })
  },
  {
    clientOnlyProduct: params({
      itemId: string().isRequired,
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: string(),
        programTiers: arrayOf(shape({
          tier: string(),
          program: string()
        }))
      })
    }).shape({
      itemId: string(),
      dataSources: string(),
      bundleSpecificationDetails: shape({
        parents: arrayOf(shape({
          parentId: string()
        }))
      }),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          ACC: bool(),
          collections: bool(),
        })
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      itemId: string(),
      fulfillment: params({ storeId: string() }).shape({
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          messages: string(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string(),
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string(),
              }),
            })
          }),
          type: string()
        }),
      }),
    })
  },
  {
    checkAvailability: alias('checkAvailability')
      .params({
        itemIds: arrayOf(string().isRequired()).isRequired(),
        zipCode: string().isRequired(),
        localStoreId: string().isRequired()
      })
      .shape({
        fulfillmentOptions: arrayOf(shape({
          fulfillable: bool(),
          services: arrayOf(
            shape({
              type: string(),
              isDefault: bool(),
              earliestAvailabilityDate: string(),
              locations: arrayOf(
                shape({
                  locationId: string()
                })
              ),
              deliveryAvailabilityDetail: arrayOf(shape({
                status: string(),
                itemId: string(),
                assortmentStrategy: string()
              }))
            })
          )
        }))
      }),
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  Discontinued,
  ProductInfoBar,
  MediaGalleryWithHotspotsDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  PriceClearanceDataModel,
  ProductConfigurator,
  Promotion,
  PromotionProductsAnchor,
  ConsumerCardMessaging,
  ProductPageEventCountdownTimer,
  SuperSku,
  MajorApplianceBuyBox,
  GlobalCustomConfigurator,
  FeatureBasedRecommendationsImporter,
  ProductOverviewAccordion,
  SpecificationsAccordion,
  QuestionsAndAnswersAccordion,
  RatingsAndReviewsAccordion,
  EmtLinksModel,
  RelatedSearchImporter,
  PopularVariationsImporter,
  DeliveryAndInstallation,
  WhatWeOffer,
  DynamicRecsWrapper,
  PipSemWrapper,
  RecentlyViewedWrapper,
  PackagesMiniABTest,
  RoomVo
);
